import React, { useEffect, useState, useRef, useCallback } from 'react';
import './IntonationComparePage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ── Types ────────────────────────────────────────────────────

interface Mismatch {
  index: number;
  text: string;
  speaker: string;
  expectedIntonation: string;
  detectedIntonation: string;
  riseHz: number;
  ratio: number;
  originalAudio: string;
  fixedAudio: string | null;
  bestTemp: number | null;
  fixRiseHz: number | null;
  fixRatio: number | null;
}

interface CourseResult {
  courseId: string;
  draftId: string;
  title: string;
  cefrLevel: string;
  totalSentences: number;
  mismatches: Mismatch[];
}

interface ScanResults {
  scanDate: string;
  summary: {
    totalCourses: number;
    totalSentences: number;
    totalMismatches: number;
    totalFixed: number;
  };
  courses: CourseResult[];
}

// ── Component ────────────────────────────────────────────────

const IntonationComparePage: React.FC = () => {
  const [data, setData] = useState<ScanResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedCourses, setExpandedCourses] = useState<Set<string>>(new Set());
  const [playingKey, setPlayingKey] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/uploads/intonation-results/results.json`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((json: ScanResults) => {
        setData(json);
        // Auto-expand courses that have mismatches
        const withMismatches = new Set<string>();
        json.courses.forEach((c) => {
          if (c.mismatches.length > 0) withMismatches.add(c.draftId);
        });
        setExpandedCourses(withMismatches);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const toggleCourse = useCallback((draftId: string) => {
    setExpandedCourses((prev) => {
      const next = new Set(prev);
      if (next.has(draftId)) next.delete(draftId);
      else next.add(draftId);
      return next;
    });
  }, []);

  const playAudio = useCallback(
    (key: string, url: string) => {
      // Stop current
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      if (playingKey === key) {
        setPlayingKey(null);
        return;
      }

      const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
      const audio = new Audio(fullUrl);
      audioRef.current = audio;
      setPlayingKey(key);

      audio.play().catch(() => setPlayingKey(null));
      audio.onended = () => {
        setPlayingKey(null);
        audioRef.current = null;
      };
    },
    [playingKey],
  );

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  if (loading) return <div className="intonation-loading">Loading results...</div>;
  if (error) return <div className="intonation-error">Failed to load results: {error}</div>;
  if (!data) return <div className="intonation-loading">No data</div>;

  const { summary, courses } = data;

  return (
    <div className="intonation-page">
      <div className="intonation-header">
        <h1>Intonation Scan Results</h1>
        <p className="intonation-desc">
          Scanned on {new Date(data.scanDate).toLocaleString()} — Comparing expected vs detected
          intonation patterns for all Inworld TTS courses.
        </p>
      </div>

      {/* Summary */}
      <div className="intonation-summary">
        <div className="intonation-stat">
          <div className="intonation-stat-value">{summary.totalCourses}</div>
          <div className="intonation-stat-label">Courses</div>
        </div>
        <div className="intonation-stat">
          <div className="intonation-stat-value">{summary.totalSentences}</div>
          <div className="intonation-stat-label">Sentences</div>
        </div>
        <div className="intonation-stat intonation-stat--warning">
          <div className="intonation-stat-value">{summary.totalMismatches}</div>
          <div className="intonation-stat-label">Mismatches</div>
        </div>
        <div className="intonation-stat intonation-stat--success">
          <div className="intonation-stat-value">{summary.totalFixed}</div>
          <div className="intonation-stat-label">Fixed</div>
        </div>
      </div>

      {/* Course list */}
      {courses.map((course) => {
        const isExpanded = expandedCourses.has(course.draftId);
        const hasMismatches = course.mismatches.length > 0;

        return (
          <div key={course.draftId} className="intonation-course">
            <div className="intonation-course-header" onClick={() => toggleCourse(course.draftId)}>
              <span
                className={`intonation-course-chevron ${isExpanded ? 'intonation-course-chevron--open' : ''}`}
              >
                &#9654;
              </span>
              <span className="intonation-course-title">{course.title}</span>
              <span className="intonation-course-cefr">{course.cefrLevel}</span>
              {hasMismatches ? (
                <span className="intonation-course-count">
                  {course.mismatches.length} / {course.totalSentences} mismatch
                </span>
              ) : (
                <span className="intonation-course-count intonation-course-match">
                  {course.totalSentences} / {course.totalSentences} match
                </span>
              )}
            </div>

            {isExpanded && hasMismatches && (
              <div className="intonation-mismatches">
                {course.mismatches.map((m) => {
                  const origKey = `${course.draftId}_${m.index}_orig`;
                  const fixKey = `${course.draftId}_${m.index}_fix`;

                  return (
                    <div key={m.index} className="intonation-row">
                      <span className="intonation-row-index">#{m.index}</span>

                      <div className="intonation-row-text">
                        {m.speaker && (
                          <span className="intonation-row-speaker">{m.speaker}: </span>
                        )}
                        {m.text}
                      </div>

                      {/* Expected vs Detected */}
                      <div className="intonation-row-arrows">
                        <span className="intonation-arrow intonation-arrow--expected">
                          {m.expectedIntonation}
                        </span>
                        <span className="intonation-arrow-sep">vs</span>
                        <span className="intonation-arrow intonation-arrow--detected">
                          {m.detectedIntonation}
                        </span>
                      </div>

                      {/* Audio buttons */}
                      <div className="intonation-audio-btns">
                        <button
                          className={`intonation-play-btn ${playingKey === origKey ? 'intonation-play-btn--playing' : ''}`}
                          onClick={() => playAudio(origKey, m.originalAudio)}
                        >
                          {playingKey === origKey ? '■' : '▶'} Original
                        </button>
                        {m.fixedAudio ? (
                          <button
                            className={`intonation-play-btn intonation-play-btn--fix ${playingKey === fixKey ? 'intonation-play-btn--playing' : ''}`}
                            onClick={() => playAudio(fixKey, m.fixedAudio!)}
                          >
                            {playingKey === fixKey ? '■' : '▶'} Fixed
                          </button>
                        ) : (
                          <span className="intonation-no-fix">No fix</span>
                        )}
                      </div>

                      {/* Metrics */}
                      <div className="intonation-metrics">
                        <span>
                          {m.riseHz.toFixed(1)}Hz / {m.ratio.toFixed(3)}
                        </span>
                        {m.fixRiseHz !== null && (
                          <>
                            <span className="intonation-metric-arrow">&rarr;</span>
                            <span className="intonation-metric--improved">
                              {m.fixRiseHz.toFixed(1)}Hz / {m.fixRatio!.toFixed(3)}
                            </span>
                          </>
                        )}
                      </div>

                      {/* Temperature badge */}
                      {m.bestTemp !== null && (
                        <span className="intonation-temp-badge">T={m.bestTemp.toFixed(1)}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {isExpanded && !hasMismatches && (
              <div style={{ padding: '16px', textAlign: 'center', color: '#059669', fontSize: 13 }}>
                All {course.totalSentences} sentences match expected intonation
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default IntonationComparePage;
